/**
 * AuthAlert — the inline, semantic alert used across the auth pages (the Novera `.alert` equivalent
 * re-skinned onto `--nv-*`). Color is NEVER the sole signal: every tone pairs a glyph + text. The
 * "red" tone is assertive (`role="alert"`) so a login/reset failure is announced; the informational
 * tones are polite (`role="status"`).
 */
import type { ReactNode } from "react";
import { AlertIcon, CheckIcon, InfoIcon } from "./icons";

export type AuthAlertTone = "red" | "cyan" | "green" | "amber";

export interface AuthAlertProps {
  tone: AuthAlertTone;
  children: ReactNode;
}

const TONE_ICON = {
  red: AlertIcon,
  cyan: InfoIcon,
  green: CheckIcon,
  amber: AlertIcon,
} as const;

export function AuthAlert({ tone, children }: AuthAlertProps) {
  const Icon = TONE_ICON[tone];
  // `amber` reuses the danger-soft shell tinted via the warning token (kept inline — single use).
  const cls = tone === "amber" ? "nv-auth-alert cyan" : `nv-auth-alert ${tone}`;
  return (
    <div className={cls} role={tone === "red" ? "alert" : "status"}>
      <Icon className="nv-auth-alert__icon" />
      <p>{children}</p>
    </div>
  );
}
